import React, { useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';

import { useLazyQuery, useMutation, NetworkStatus } from '@apollo/client';
import { ToastContainer, toast } from 'react-toastify';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import Search from './Search/Search';
import AdminManagementConfigGrid from './Grid/Grid';
import SubHeader from '../../../Header/SubHeader';

import * as Q from '../../../../constants/graphqlQueries';

import AdminDetails from './SearchDetails/AdminDetails';
import AccessDetails from './SearchDetails/AccessDetails';
import UserRoles from '../../../../constants/userRoles';

import './AdminSearch.css';
import 'react-toastify/dist/ReactToastify.css';
import transactionResponse from '../../../../constants/transactionResponse';
import SwitchCustom from '../../../Switch/SwitchCustom';

const gridTabs = [
  {
    index: 0,
    label: 'General Users'
  },
  {
    index: 1,
    label: 'WFM Users'
  },
];

const messagesTypes = {
  dataNotFound: 'dataNotFound',
}

// If the search hasn't resolved within this window, surface a clear message to the
// user instead of leaving the grid spinning silently with no feedback.
const SEARCH_TIMEOUT_MS = 15000;

const AdminSearch = () => {
  const [timerOnRefresh, setTimerOnRefresh] = useState();
  const searchTimeoutRef = useRef(null);
  const [employeesDataPopulation, setEmployeesDataPopulation] = useState([]);
  const [selectedOptionMenu, setSelectedOptionMenu] = useState('');
  const [roleEnable, setRoleEnable] = useState(true);

  const [getEmployeeData, {
      data: employeesData,
      loading,
      refetch: refetchEmployeeData,
      error: getEmployeeDataError,
      networkStatus: getEmployeeDataNetworkStatus
  }] = useLazyQuery(
    Q.ADMIN_MANAGMENT_EMPLOYEE_DATA, {
      notifyOnNetworkStatusChange: true
  });

  const [grantedAdminAccess, { data: grantedAccessResponse }] = useMutation(Q.GRANTED_ADMIN_ACCESS);
  const [revokedAdminAccess, { data: revokedAccessResponse }] = useMutation(Q.REVOKED_ADMIN_ACCESS);

  const handleAdminAccess = (status, value, toggleAdminSwitch) => {
    clearTimeout(timerOnRefresh);
    toggleAdminSwitch();
    if (status) grantedAdminAccess({ variables: { EmployeeId: value } });
    else revokedAdminAccess({ variables: { EmployeeId: value } });
  }

  /* eslint-disable quotes */
  const gridConfigurations = {
    GENERAL_USERS: {
      keyGrid: 1,
      columns: [
        { title: 'Employee Name', field: `person.FirstName`, width: '600px', render: rowData => `${rowData.person.FirstName} ${rowData.person.LastName}` },
        { title: 'Oracle ID', field: 'EmployeeId', width: '270px' },
        { title: 'Role', field: 'JobRole', width: '50px' },
        { title: 'Site', field: 'SiteDescription', width: '600px' },
        { title: 'Agents', field: 'AgentsCount.Amount', width: '50px', render: rowData => rowData.JobRole !== UserRoles['G&A'] ? 'N/A' : rowData.AgentsCount.Amount },
        { title: 'Reports to', field: 'SupervisorFirstName', width: '450px', render: rowData => `${rowData.SupervisorFirstName} ${rowData.SupervisorLastName}` }
      ],
      options: {
        defaultExpanded: true,
        filtering: false,
        actionsColumnIndex: 0,
        tableLayout: 'auto',
        actionsCellStyle: {
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        },
      },
      localization: {
        header: {
          actions: 'Actions',
        },
        body: {
          emptyDataSourceMessage: 'Search to view details',
        },
      },
      detailPanel: [
        (rowData) => (
          {
          icon: () => <ArrowRightIcon />,
          openIcon: () => <ArrowDropDownIcon />,
          disabled: rowData.JobRole !== UserRoles['G&A'] && rowData.JobRole !== UserRoles.WFM || rowData.AgentsCount.Amount === 0,
          render: () => <AdminDetails rowData={rowData} />
        })
      ]
    },
    WFM_USERS: {
      keyGrid: 2,
      columns: [
        { title: 'Employee Name', field: `person.FirstName`, width: '25%', render: rowData => `${rowData.person.FirstName} ${rowData.person.LastName}` },
        { title: 'Oracle ID', field: 'EmployeeId', width: '15%' },
        { title: 'Job Title', field: 'person.Title', width: '25%' },
        { title: 'Site', field: 'SiteDescription', width: '25%' },
        { title: 'Admin Access', width: '10%', render: rowData => {
          const { admin, EmployeeId } = rowData;
          let adminStatus = false;
          if (admin) adminStatus = true;
          return <SwitchCustom switchValue={EmployeeId} switchStatus={adminStatus} handleSwitch={handleAdminAccess} validateCheckedStatus />
        }},
      ],
      options: {
        defaultExpanded: true,
        filtering: false,
        actionsColumnIndex: 0,
        tableLayout: 'auto',
        actionsCellStyle: {
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
        },
      },
      localization: {
        header: {
          actions: 'Actions',
        },
        body: {
          emptyDataSourceMessage: 'Search WFM to view details',
        },
      },
      detailPanel: rowData => <AccessDetails rowData={rowData} />
    }
  }

  const [gridConfiguration, setGridConfiguration] = useState(gridConfigurations.GENERAL_USERS);

  const onSearchClick = (employeeFirstName, employeeLastName, employeeId, jobRole, jobSite) => {
    let role = jobRole ? jobRole.JobRole : null;
    // If the active tab is WFM Users, set WFM as role for the search
    if (selectedOptionMenu === gridTabs[1].label) {
      role = UserRoles.WFM;
    }

    // Start a safety-net timeout so the user gets clear feedback if the request
    // hangs, instead of an indefinitely spinning grid with no message.
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      toast.error('Search is taking longer than expected. Try narrowing your filters (e.g. add a last name or site) and search again.');
    }, SEARCH_TIMEOUT_MS);

    getEmployeeData({
      variables: {
        EmployeeFirstName: employeeFirstName || null,
        EmployeeLastName: employeeLastName || null,
        EmployeeId: employeeId || null,
        JobRole: role || null,
        JobSite: jobSite.SiteId || null
      }
    });
  }

  const onClearEmployeeData = () => {
    setEmployeesDataPopulation([]);
  }

  const handleSelected = (choice) => {
    if (choice.label === gridTabs[0].label) {
      setGridConfiguration(gridConfigurations.GENERAL_USERS)
      setRoleEnable(true)
    }
    if (choice.label === gridTabs[1].label) {
      setGridConfiguration(gridConfigurations.WFM_USERS)
      setRoleEnable(false);
    }
    onClearEmployeeData();
    setSelectedOptionMenu(choice.label);
  }

  useEffect(() => {
    if (employeesData && getEmployeeDataNetworkStatus === NetworkStatus.ready) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      if (employeesData.getEmployeeData.length) {
        setEmployeesDataPopulation(employeesData.getEmployeeData);
      }
      else if (!(employeesData.getEmployeeData?.length)) {
        toast(transactionResponse(null, null, messagesTypes.dataNotFound));
        setEmployeesDataPopulation([]);
      }
    }
  }, [employeesData, getEmployeeDataNetworkStatus]);

  useEffect(() => {
    if (getEmployeeDataError) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = null;
      }
      toast(transactionResponse(null, null, messagesTypes.dataNotFound));
    }
  }, [getEmployeeDataError]);

  // Clean up any pending search timeout on unmount.
  useEffect(() => () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  useEffect(() => {
    if (grantedAccessResponse)
      if (grantedAccessResponse.grantAdminAccess) {
        const timerId = setTimeout(() => refetchEmployeeData(), 500);
        setTimerOnRefresh(timerId);
      }
  }, [grantedAccessResponse]);

  useEffect(() => {
    if (revokedAccessResponse) {
      if (revokedAccessResponse.revokeAdminAccess) {
        const timerId = setTimeout(() => refetchEmployeeData(), 500);
        setTimerOnRefresh(timerId);
      }
    }
  }, [revokedAccessResponse]);

  return (
    <Box className='__adminManagement'>
      <div className='__adminManagement__filters'>
        <Search
          roleEnable={roleEnable}
          onClearEmployeeData={onClearEmployeeData}
          onSearchClick={onSearchClick}
        />
      </div>
      <div className='__adminManagement__grid'>
        <SubHeader
          handleSelected={handleSelected}
          options={gridTabs}
          selectedOptions={selectedOptionMenu}
          isDisabled={loading}
        />
        <AdminManagementConfigGrid
          keyGrid={gridConfiguration.keyGrid}
          columns={gridConfiguration.columns}
          actions={gridConfiguration.actions}
          options={gridConfiguration.options}
          localization={gridConfiguration.localization}
          detailPanel={gridConfiguration.detailPanel}
          isLoading={loading}
          data={employeesDataPopulation}
        />
      </div>
      <ToastContainer hideProgressBar />
    </Box>
  );
}

export default AdminSearch;
